'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { ArrowLeft, Search, Download, Eye, CheckCircle2, XCircle, AlertTriangle, Clock, FileText, X, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, RotateCcw, DollarSign, Package, Plus,  } from 'lucide-react';
import {
  MOCK_INVOICES, MOCK_PURCHASE_ORDERS, MOCK_GRNS,
  SupplierInvoice, InvoiceStatus, formatUGX,
} from '../../components/procurementData';
import { useRole } from '@/lib/useRole';
import Icon from '@/components/ui/AppIcon';


// ─── Extended mock invoice data with richer three-way match details ───────────

interface MatchLineItem {
  id: string;
  description: string;
  unit: string;
  poQty: number;
  poUnitPrice: number;
  poLineTotal: number;
  grnQty: number;
  grnAccepted: number;
  invQty: number;
  invUnitPrice: number;
  invLineTotal: number;
  qtyVariance: number;
  priceVariance: number;
  totalVariance: number;
  varianceType: 'none' | 'minor' | 'major';
}

interface ThreeWayMatch {
  invoiceId: string;
  poTotal: number;
  grnAcceptedTotal: number;
  invoiceTotal: number;
  totalVariance: number;
  variancePct: number;
  matchResult: 'matched' | 'minor_variance' | 'mismatch' | 'pending';
  lineItems: MatchLineItem[];
  approvalHistory: {
    id: string;
    action: 'approved' | 'rejected' | 'returned' | 'pending';
    approver: string;
    role: string;
    date?: string;
    comments?: string;
  }[];
}

const THREE_WAY_MATCHES: ThreeWayMatch[] = [
  {
    invoiceId: 'inv-001',
    poTotal: 1807500,
    grnAcceptedTotal: 1807500,
    invoiceTotal: 1807500,
    totalVariance: 0,
    variancePct: 0,
    matchResult: 'matched',
    lineItems: [
      {
        id: 'li-001', description: 'Protective Overalls', unit: 'piece',
        poQty: 20, poUnitPrice: 45000, poLineTotal: 855000,
        grnQty: 20, grnAccepted: 20,
        invQty: 20, invUnitPrice: 45000, invLineTotal: 855000,
        qtyVariance: 0, priceVariance: 0, totalVariance: 0, varianceType: 'none',
      },
      {
        id: 'li-002', description: 'Rubber Boots (pairs)', unit: 'pair',
        poQty: 20, poUnitPrice: 35000, poLineTotal: 665000,
        grnQty: 20, grnAccepted: 19,
        invQty: 19, invUnitPrice: 35000, invLineTotal: 665000,
        qtyVariance: -1, priceVariance: 0, totalVariance: 0, varianceType: 'none',
      },
      {
        id: 'li-003', description: 'Face Masks (box)', unit: 'box',
        poQty: 10, poUnitPrice: 25000, poLineTotal: 237500,
        grnQty: 10, grnAccepted: 10,
        invQty: 10, invUnitPrice: 25000, invLineTotal: 237500,
        qtyVariance: 0, priceVariance: 0, totalVariance: 0, varianceType: 'none',
      },
    ],
    approvalHistory: [
      { id: 'ah-001', action: 'approved', approver: 'Nakato Sarah', role: 'Accounts Payable', date: '2026-09-06', comments: 'All three documents match. Approved for payment.' },
    ],
  },
  {
    invoiceId: 'inv-002',
    poTotal: 3600000,
    grnAcceptedTotal: 3600000,
    invoiceTotal: 3600000,
    totalVariance: 0,
    variancePct: 0,
    matchResult: 'matched',
    lineItems: [
      {
        id: 'li-004', description: 'Newcastle Disease Vaccine (La Sota)', unit: 'dose',
        poQty: 3000, poUnitPrice: 1200, poLineTotal: 3600000,
        grnQty: 3000, grnAccepted: 3000,
        invQty: 3000, invUnitPrice: 1200, invLineTotal: 3600000,
        qtyVariance: 0, priceVariance: 0, totalVariance: 0, varianceType: 'none',
      },
    ],
    approvalHistory: [
      { id: 'ah-002', action: 'approved', approver: 'Mugisha Robert', role: 'Finance Manager', date: '2026-09-05', comments: 'Perfect match. Payment processed.' },
    ],
  },
  {
    invoiceId: 'inv-003',
    poTotal: 24700000,
    grnAcceptedTotal: 14700000,
    invoiceTotal: 14700000,
    totalVariance: 50000,
    variancePct: 0.34,
    matchResult: 'minor_variance',
    lineItems: [
      {
        id: 'li-005', description: 'Broiler Finisher Feed (50kg bags)', unit: 'bag',
        poQty: 200, poUnitPrice: 125000, poLineTotal: 24500000,
        grnQty: 120, grnAccepted: 120,
        invQty: 120, invUnitPrice: 125417, invLineTotal: 15050000,
        qtyVariance: 0, priceVariance: 417, totalVariance: 50000, varianceType: 'minor',
      },
    ],
    approvalHistory: [
      { id: 'ah-003', action: 'pending', approver: 'Nakato Sarah', role: 'Accounts Payable', comments: '' },
    ],
  },
];

// ─── Extended invoices with additional mock data ──────────────────────────────

const EXTENDED_INVOICES: SupplierInvoice[] = [
  ...MOCK_INVOICES,
  {
    id: 'inv-004',
    invoiceNumber: 'INV-EAA-20260910',
    supplier: 'East Africa Agri-Inputs',
    poNumber: 'PO-2026-0126',
    poId: 'po-004',
    grnNumber: '—',
    invoiceDate: '2026-09-10',
    dueDate: '2026-09-10',
    currency: 'UGX',
    subtotal: 1200000,
    tax: 0,
    discount: 0,
    total: 1280000,
    status: 'mismatch',
    matchStatus: 'mismatch',
    paymentStatus: 'unpaid',
    notes: 'Invoice received but GRN not yet created. Cannot complete three-way match.',
    createdAt: '2026-09-10T09:00:00Z',
  },
  {
    id: 'inv-005',
    invoiceNumber: 'INV-NAS-20260901',
    supplier: 'Nile Agro Supplies Ltd',
    poNumber: 'PO-2026-0121',
    poId: 'po-005',
    grnNumber: 'GRN-2026-0030',
    invoiceDate: '2026-09-01',
    dueDate: '2026-09-15',
    currency: 'UGX',
    subtotal: 8500000,
    tax: 0,
    discount: 0,
    total: 8500000,
    status: 'pending',
    matchStatus: 'pending',
    paymentStatus: 'unpaid',
    notes: 'Awaiting three-way match verification.',
    createdAt: '2026-09-01T11:00:00Z',
  },
];

const EXTENDED_MATCHES: ThreeWayMatch[] = [
  ...THREE_WAY_MATCHES,
  {
    invoiceId: 'inv-004',
    poTotal: 1280000,
    grnAcceptedTotal: 0,
    invoiceTotal: 1280000,
    totalVariance: 1280000,
    variancePct: 100,
    matchResult: 'mismatch',
    lineItems: [
      {
        id: 'li-006', description: 'Disinfectant (Virkon S)', unit: 'kg',
        poQty: 20, poUnitPrice: 45000, poLineTotal: 900000,
        grnQty: 0, grnAccepted: 0,
        invQty: 20, invUnitPrice: 45000, invLineTotal: 900000,
        qtyVariance: -20, priceVariance: 0, totalVariance: -900000, varianceType: 'major',
      },
      {
        id: 'li-007', description: 'Fumigation Chemicals', unit: 'litre',
        poQty: 10, poUnitPrice: 30000, poLineTotal: 300000,
        grnQty: 0, grnAccepted: 0,
        invQty: 10, invUnitPrice: 30000, invLineTotal: 300000,
        qtyVariance: -10, priceVariance: 0, totalVariance: -300000, varianceType: 'major',
      },
    ],
    approvalHistory: [
      { id: 'ah-004', action: 'returned', approver: 'Nakato Sarah', role: 'Accounts Payable', date: '2026-09-11', comments: 'GRN missing. Cannot approve payment without goods receipt confirmation.' },
    ],
  },
  {
    invoiceId: 'inv-005',
    poTotal: 8500000,
    grnAcceptedTotal: 8500000,
    invoiceTotal: 8500000,
    totalVariance: 0,
    variancePct: 0,
    matchResult: 'pending',
    lineItems: [
      {
        id: 'li-008', description: 'Layer Mash Feed (50kg bags)', unit: 'bag',
        poQty: 68, poUnitPrice: 125000, poLineTotal: 8500000,
        grnQty: 68, grnAccepted: 68,
        invQty: 68, invUnitPrice: 125000, invLineTotal: 8500000,
        qtyVariance: 0, priceVariance: 0, totalVariance: 0, varianceType: 'none',
      },
    ],
    approvalHistory: [
      { id: 'ah-005', action: 'pending', approver: 'Nakato Sarah', role: 'Accounts Payable', comments: '' },
    ],
  },
];

// ─── Status Styles ────────────────────────────────────────────────────────────

const INVOICE_STATUS_STYLES: Record<InvoiceStatus, { label: string; bg: string; text: string; dot: string }> = {
  pending: { label: 'Pending', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
  matched: { label: 'Matched', bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  minor_variance: { label: 'Minor Variance', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  mismatch: { label: 'Mismatch', bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  approved: { label: 'Approved', bg: 'bg-teal-50', text: 'text-teal-700', dot: 'bg-teal-500' },
  paid: { label: 'Paid', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  overdue: { label: 'Overdue', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-600' },
};

const PAYMENT_STATUS_STYLES: Record<string, { label: string; bg: string; text: string }> = {
  unpaid: { label: 'Unpaid', bg: 'bg-gray-100', text: 'text-gray-600' },
  partially_paid: { label: 'Partially Paid', bg: 'bg-blue-50', text: 'text-blue-700' },
  paid: { label: 'Paid', bg: 'bg-emerald-50', text: 'text-emerald-700' },
  overdue: { label: 'Overdue', bg: 'bg-red-50', text: 'text-red-700' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function InvoiceStatusBadge({ status }: { status: InvoiceStatus }) {
  const st = INVOICE_STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
      {st.label}
    </span>
  );
}

function MatchResultIcon({ result }: { result: string }) {
  if (result === 'matched') return <CheckCircle2 size={15} className="text-green-600" />;
  if (result === 'minor_variance') return <AlertTriangle size={15} className="text-amber-500" />;
  if (result === 'mismatch') return <XCircle size={15} className="text-red-500" />;
  return <Clock size={15} className="text-gray-400" />;
}

function VarianceCell({ value, pct }: { value: number; pct?: number }) {
  if (value === 0) return <span className="text-green-600 font-semibold text-xs">—</span>;
  const isNeg = value < 0;
  return (
    <div className={`text-xs font-semibold ${isNeg ? 'text-red-600' : 'text-amber-600'}`}>
      {isNeg ? '▼' : '▲'} {formatUGX(Math.abs(value))}
      {pct !== undefined && <span className="ml-1 opacity-70">({Math.abs(pct).toFixed(1)}%)</span>}
    </div>
  );
}

// ─── Approval Modal ───────────────────────────────────────────────────────────

function ApprovalModal({
  invoice,
  match,
  onClose,
  onAction,
}: {
  invoice: SupplierInvoice;
  match: ThreeWayMatch;
  onClose: () => void;
  onAction: (invoiceId: string, action: 'approved' | 'rejected' | 'returned') => void;
}) {
  const [action, setAction] = useState<'approved' | 'rejected' | 'returned' | null>(null);
  const [comments, setComments] = useState('');
  const [submitted, setSubmitted] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!action) return;
    if ((action === 'rejected' || action === 'returned') && !comments.trim()) return;
    setSubmitted(true);
    setTimeout(() => { onAction(invoice.id, action); onClose(); }, 1200);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Invoice Approval Decision</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={28} className="text-green-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-1">Decision Recorded</h3>
            <p className="text-sm text-gray-500">The invoice has been {action}. Accounts Payable has been notified.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Invoice summary */}
            <div className="bg-gray-50 rounded-xl p-3 text-sm">
              <div className="font-bold text-gray-900">{invoice.invoiceNumber}</div>
              <div className="text-gray-500 text-xs mt-0.5">{invoice.supplier} · {formatUGX(invoice.total)}</div>
              <div className="flex items-center gap-2 mt-2">
                <MatchResultIcon result={match.matchResult} />
                <span className="text-xs font-semibold text-gray-700">
                  {match.matchResult === 'matched' ? 'Three-way match confirmed' :
                   match.matchResult === 'minor_variance' ? `Minor variance: ${formatUGX(Math.abs(match.totalVariance))} (${match.variancePct.toFixed(2)}%)` :
                   match.matchResult === 'mismatch' ? 'Critical mismatch — GRN missing or quantities differ significantly' :
                   'Pending verification'}
                </span>
              </div>
            </div>

            {/* Action selection */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-2">Select Action *</label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: 'approved', label: 'Approve', icon: ThumbsUp, color: 'border-green-300 bg-green-50 text-green-700' },
                  { value: 'returned', label: 'Return', icon: RotateCcw, color: 'border-amber-300 bg-amber-50 text-amber-700' },
                  { value: 'rejected', label: 'Reject', icon: ThumbsDown, color: 'border-red-300 bg-red-50 text-red-700' },
                ] as const).map(opt => {
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setAction(opt.value)}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-xs font-semibold ${
                        action === opt.value ? opt.color + ' ring-2 ring-offset-1 ring-current' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Icon size={16} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1.5">
                Comments {(action === 'rejected' || action === 'returned') ? '*' : '(optional)'}
              </label>
              <textarea
                value={comments}
                onChange={e => setComments(e.target.value)}
                rows={3}
                required={action === 'rejected' || action === 'returned'}
                placeholder={
                  action === 'approved' ? 'Optional approval notes...' :
                  action === 'returned' ? 'Explain what needs to be corrected...' :
                  action === 'rejected' ? 'State the reason for rejection...' :
                  'Select an action above...'
                }
                className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 resize-none"
              />
            </div>

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
                Cancel
              </button>
              <button
                type="submit"
                disabled={!action}
                className="flex-1 py-2.5 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Confirm Decision
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Three-Way Match Drawer ───────────────────────────────────────────────────

function MatchDrawer({
  invoice,
  match,
  onClose,
  onApprove,
}: {
  invoice: SupplierInvoice;
  match: ThreeWayMatch;
  onClose: () => void;
  onApprove: () => void;
}) {
  const [expandedItems, setExpandedItems] = useState(true);
  const { can } = useRole();
  const canApprove = can('manage_procurement');

  const po = MOCK_PURCHASE_ORDERS.find(p => p.id === invoice.poId);
  const grn = MOCK_GRNS.find(g => g.poId === invoice.poId);

  const matchColor =
    match.matchResult === 'matched' ? 'bg-green-50 border-green-200 text-green-700' :
    match.matchResult === 'minor_variance' ? 'bg-amber-50 border-amber-200 text-amber-700' :
    match.matchResult === 'mismatch'? 'bg-red-50 border-red-200 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-600';

  const matchLabel =
    match.matchResult === 'matched' ? '✓ Three-Way Match Confirmed' :
    match.matchResult === 'minor_variance' ? '⚠ Minor Variance Detected' :
    match.matchResult === 'mismatch'? '✗ Match Failed — Review Required' : '○ Pending Verification';

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <div className="text-base font-bold text-gray-900">{invoice.invoiceNumber}</div>
            <div className="text-xs text-gray-400 mt-0.5">{invoice.supplier} · {invoice.invoiceDate}</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="flex-1 p-5 space-y-5">
          {/* Match result banner */}
          <div className={`flex items-center gap-3 p-3 rounded-xl border ${matchColor}`}>
            <MatchResultIcon result={match.matchResult} />
            <div className="flex-1">
              <div className="text-sm font-bold">{matchLabel}</div>
              {match.totalVariance !== 0 && (
                <div className="text-xs mt-0.5">
                  Total variance: {formatUGX(Math.abs(match.totalVariance))} ({match.variancePct.toFixed(2)}%)
                </div>
              )}
            </div>
            <InvoiceStatusBadge status={invoice.status} />
          </div>

          {/* Three-way summary cards */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Purchase Order', ref: invoice.poNumber, total: match.poTotal, icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Goods Received', ref: invoice.grnNumber, total: match.grnAcceptedTotal, icon: Package, color: 'text-teal-600', bg: 'bg-teal-50' },
              { label: 'Supplier Invoice', ref: invoice.invoiceNumber, total: match.invoiceTotal, icon: DollarSign, color: 'text-orange-600', bg: 'bg-orange-50' },
            ].map(card => {
              const Icon = card.icon;
              return (
                <div key={card.label} className="bg-white border border-gray-100 rounded-xl p-3">
                  <div className={`w-8 h-8 ${card.bg} rounded-lg flex items-center justify-center mb-2`}>
                    <Icon size={15} className={card.color} />
                  </div>
                  <div className="text-xs text-gray-400 font-medium">{card.label}</div>
                  <div className="text-xs font-bold text-gray-600 mt-0.5 truncate">{card.ref}</div>
                  <div className="text-sm font-bold text-gray-900 mt-1">{formatUGX(card.total)}</div>
                </div>
              );
            })}
          </div>

          {/* Document details */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Supplier', value: invoice.supplier },
              { label: 'Invoice Date', value: invoice.invoiceDate },
              { label: 'Due Date', value: invoice.dueDate },
              { label: 'Payment Terms', value: po?.paymentTerms || '—' },
              { label: 'Farm', value: po?.farm || '—' },
              { label: 'Department', value: po?.department || '—' },
            ].map(f => (
              <div key={f.label}>
                <div className="text-xs text-gray-400 font-medium">{f.label}</div>
                <div className="text-sm font-semibold text-gray-800 mt-0.5">{f.value}</div>
              </div>
            ))}
          </div>

          {/* Line-item comparison table */}
          <div>
            <button
              onClick={() => setExpandedItems(v => !v)}
              className="flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 hover:text-gray-900 transition-colors"
            >
              {expandedItems ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              Line-Item Three-Way Comparison
            </button>

            {expandedItems && (
              <div className="overflow-x-auto rounded-xl border border-gray-100">
                <table className="w-full text-xs min-w-[640px]">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-3 py-2.5 font-bold text-gray-500 uppercase tracking-wide">Item</th>
                      <th className="text-center px-3 py-2.5 font-bold text-blue-500 uppercase tracking-wide" colSpan={2}>PO</th>
                      <th className="text-center px-3 py-2.5 font-bold text-teal-500 uppercase tracking-wide" colSpan={2}>GRN</th>
                      <th className="text-center px-3 py-2.5 font-bold text-orange-500 uppercase tracking-wide" colSpan={2}>Invoice</th>
                      <th className="text-center px-3 py-2.5 font-bold text-gray-500 uppercase tracking-wide">Variance</th>
                    </tr>
                    <tr className="bg-gray-50/50 border-b border-gray-100">
                      <th className="text-left px-3 py-1.5 text-gray-400 font-medium"></th>
                      <th className="text-right px-3 py-1.5 text-blue-400 font-medium">Qty</th>
                      <th className="text-right px-3 py-1.5 text-blue-400 font-medium">Unit Price</th>
                      <th className="text-right px-3 py-1.5 text-teal-400 font-medium">Delivered</th>
                      <th className="text-right px-3 py-1.5 text-teal-400 font-medium">Accepted</th>
                      <th className="text-right px-3 py-1.5 text-orange-400 font-medium">Qty</th>
                      <th className="text-right px-3 py-1.5 text-orange-400 font-medium">Unit Price</th>
                      <th className="text-right px-3 py-1.5 text-gray-400 font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {match.lineItems.map(item => {
                      const hasVariance = item.varianceType !== 'none';
                      const rowBg = item.varianceType === 'major' ? 'bg-red-50/40' : item.varianceType === 'minor' ? 'bg-amber-50/40' : '';
                      return (
                        <tr key={item.id} className={`${rowBg} hover:bg-gray-50/50 transition-colors`}>
                          <td className="px-3 py-2.5">
                            <div className="font-semibold text-gray-800">{item.description}</div>
                            <div className="text-gray-400">{item.unit}</div>
                          </td>
                          <td className="px-3 py-2.5 text-right text-blue-700 font-medium">{item.poQty}</td>
                          <td className="px-3 py-2.5 text-right text-blue-700 font-medium">{formatUGX(item.poUnitPrice)}</td>
                          <td className="px-3 py-2.5 text-right text-teal-700 font-medium">{item.grnQty}</td>
                          <td className="px-3 py-2.5 text-right text-teal-700 font-medium">{item.grnAccepted}</td>
                          <td className={`px-3 py-2.5 text-right font-medium ${item.qtyVariance !== 0 ? 'text-red-600' : 'text-orange-700'}`}>
                            {item.invQty}
                            {item.qtyVariance !== 0 && <span className="ml-1 text-red-500">({item.qtyVariance > 0 ? '+' : ''}{item.qtyVariance})</span>}
                          </td>
                          <td className={`px-3 py-2.5 text-right font-medium ${item.priceVariance !== 0 ? 'text-amber-600' : 'text-orange-700'}`}>
                            {formatUGX(item.invUnitPrice)}
                            {item.priceVariance !== 0 && <span className="ml-1 text-amber-500">(+{formatUGX(item.priceVariance)})</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right">
                            {hasVariance ? (
                              <VarianceCell value={item.totalVariance} />
                            ) : (
                              <span className="text-green-600 font-semibold">✓</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 bg-gray-50">
                      <td className="px-3 py-2.5 font-bold text-gray-700" colSpan={2}>Totals</td>
                      <td className="px-3 py-2.5 text-right font-bold text-blue-700">{formatUGX(match.poTotal)}</td>
                      <td className="px-3 py-2.5" colSpan={2}></td>
                      <td className="px-3 py-2.5 text-right font-bold text-orange-700" colSpan={2}>{formatUGX(match.invoiceTotal)}</td>
                      <td className="px-3 py-2.5 text-right">
                        <VarianceCell value={match.totalVariance} pct={match.variancePct} />
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Approval history */}
          <div>
            <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Approval History</div>
            <div className="space-y-2">
              {match.approvalHistory.map(h => (
                <div key={h.id} className={`flex items-start gap-3 p-3 rounded-xl border ${
                  h.action === 'approved' ? 'bg-green-50 border-green-100' :
                  h.action === 'rejected' ? 'bg-red-50 border-red-100' :
                  h.action === 'returned'? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'
                }`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                    h.action === 'approved' ? 'bg-green-100' :
                    h.action === 'rejected' ? 'bg-red-100' :
                    h.action === 'returned'? 'bg-amber-100' : 'bg-gray-100'
                  }`}>
                    {h.action === 'approved' ? <CheckCircle2 size={13} className="text-green-600" /> :
                     h.action === 'rejected' ? <XCircle size={13} className="text-red-500" /> :
                     h.action === 'returned' ? <RotateCcw size={13} className="text-amber-600" /> :
                     <Clock size={13} className="text-gray-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold text-gray-800">{h.approver}</span>
                      <span className="text-xs text-gray-400">{h.role}</span>
                      {h.date && <span className="text-xs text-gray-400">{h.date}</span>}
                      <span className={`text-xs font-semibold capitalize px-2 py-0.5 rounded-full ${
                        h.action === 'approved' ? 'bg-green-100 text-green-700' :
                        h.action === 'rejected' ? 'bg-red-100 text-red-700' :
                        h.action === 'returned'? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                      }`}>{h.action === 'pending' ? 'Awaiting Decision' : h.action}</span>
                    </div>
                    {h.comments && <div className="text-xs text-gray-600 mt-1 italic">"{h.comments}"</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {invoice.notes && (
            <div>
              <div className="text-xs font-bold text-gray-700 mb-1">Notes</div>
              <div className="text-sm text-gray-600 bg-gray-50 rounded-xl p-3">{invoice.notes}</div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-gray-100 flex gap-2 flex-wrap">
          <button className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 text-xs font-semibold rounded-xl hover:bg-gray-50 transition-colors text-gray-700">
            <Download size={13} /> Export PDF
          </button>
          {canApprove && (invoice.status === 'pending' || invoice.status === 'matched' || invoice.status === 'minor_variance') && (
            <button
              onClick={onApprove}
              className="ml-auto flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-xs font-semibold rounded-xl hover:bg-orange-700 transition-colors"
            >
              <CheckCircle2 size={13} /> Process Approval
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InvoicesContent() {
  const { can } = useRole();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [matchFilter, setMatchFilter] = useState('all');
  const [selected, setSelected] = useState<SupplierInvoice | null>(null);
  const [approvalTarget, setApprovalTarget] = useState<SupplierInvoice | null>(null);
  const [invoices, setInvoices] = useState(EXTENDED_INVOICES);

  const canApprove = can('manage_procurement');

  const filtered = useMemo(() => {
    return invoices.filter(inv => {
      const q = search.toLowerCase();
      const matchSearch = !q ||
        inv.invoiceNumber.toLowerCase().includes(q) ||
        inv.supplier.toLowerCase().includes(q) ||
        inv.poNumber.toLowerCase().includes(q) ||
        inv.grnNumber.toLowerCase().includes(q);
      const matchStatus = statusFilter === 'all' || inv.status === statusFilter;
      const matchMatch = matchFilter === 'all' || inv.matchStatus === matchFilter;
      return matchSearch && matchStatus && matchMatch;
    });
  }, [invoices, search, statusFilter, matchFilter]);

  const stats = {
    total: invoices.length,
    matched: invoices.filter(i => i.matchStatus === 'matched').length,
    minorVariance: invoices.filter(i => i.matchStatus === 'minor_variance').length,
    mismatch: invoices.filter(i => i.matchStatus === 'mismatch').length,
    pending: invoices.filter(i => i.matchStatus === 'pending').length,
    totalValue: invoices.reduce((s, i) => s + i.total, 0),
    unpaidValue: invoices.filter(i => i.paymentStatus === 'unpaid').reduce((s, i) => s + i.total, 0),
  };

  function handleApprovalAction(invoiceId: string, action: 'approved' | 'rejected' | 'returned') {
    setInvoices(prev => prev.map(inv => {
      if (inv.id !== invoiceId) return inv;
      const newStatus: InvoiceStatus = action === 'approved' ? 'approved' : action === 'rejected' ? 'mismatch' : 'pending';
      return { ...inv, status: newStatus };
    }));
    setSelected(null);
  }

  const selectedMatch = selected ? EXTENDED_MATCHES.find(m => m.invoiceId === selected.id) : null;
  const approvalMatch = approvalTarget ? EXTENDED_MATCHES.find(m => m.invoiceId === approvalTarget.id) : null;

  return (
    <div className="px-4 md:px-6 xl:px-8 py-6 max-w-screen-2xl mx-auto">

      {/* Header */}
      <div className="flex items-center gap-3 mb-1">
        <Link href="/procurement" className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={16} className="text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">Invoice Matching</h1>
          <p className="text-xs text-gray-400 mt-0.5">PO vs GRN vs Invoice three-way validation — Idudi Poultry Farm</p>
        </div>
        {canApprove && (
          <button className="flex items-center gap-1.5 px-4 py-2 bg-orange-600 text-white text-sm font-semibold rounded-xl hover:bg-orange-700 transition-colors shadow-sm">
            <Plus size={14} /> Register Invoice
          </button>
        )}
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 my-5">
        {[
          { label: 'Total Invoices', value: stats.total, color: 'text-gray-900', bg: '' },
          { label: 'Matched', value: stats.matched, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'Minor Variance', value: stats.minorVariance, color: 'text-amber-700', bg: 'bg-amber-50' },
          { label: 'Mismatch', value: stats.mismatch, color: 'text-red-700', bg: 'bg-red-50' },
          { label: 'Pending', value: stats.pending, color: 'text-gray-600', bg: 'bg-gray-50' },
          { label: 'Total Value', value: `${(stats.totalValue / 1000000).toFixed(1)}M`, color: 'text-indigo-700', bg: 'bg-indigo-50' },
          { label: 'Unpaid', value: `${(stats.unpaidValue / 1000000).toFixed(1)}M`, color: 'text-orange-700', bg: 'bg-orange-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg || 'bg-white'} rounded-xl border border-gray-100 p-3 text-center`}>
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Match status legend */}
      <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 rounded-xl border border-gray-100">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wide self-center">Match Legend:</span>
        {[
          { icon: <CheckCircle2 size={13} className="text-green-600" />, label: 'Matched — PO, GRN & Invoice align', color: 'text-green-700' },
          { icon: <AlertTriangle size={13} className="text-amber-500" />, label: 'Minor Variance — within tolerance', color: 'text-amber-700' },
          { icon: <XCircle size={13} className="text-red-500" />, label: 'Mismatch — requires investigation', color: 'text-red-700' },
          { icon: <Clock size={13} className="text-gray-400" />, label: 'Pending — awaiting verification', color: 'text-gray-600' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1.5">
            {l.icon}
            <span className={`text-xs font-medium ${l.color}`}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search invoices, suppliers, PO or GRN numbers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-700"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="matched">Matched</option>
          <option value="minor_variance">Minor Variance</option>
          <option value="mismatch">Mismatch</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </select>
        <select
          value={matchFilter}
          onChange={e => setMatchFilter(e.target.value)}
          className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white text-gray-700"
        >
          <option value="all">All Match Results</option>
          <option value="matched">Matched</option>
          <option value="minor_variance">Minor Variance</option>
          <option value="mismatch">Mismatch</option>
          <option value="pending">Pending</option>
        </select>
        <button className="flex items-center gap-1.5 px-3 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-600">
          <Download size={14} /> Export
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Invoice</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Supplier</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">PO / GRN</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Match</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Invoice Total</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Variance</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Payment</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Due Date</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="text-center py-12 text-gray-400">
                    <FileText size={32} className="mx-auto mb-2 text-gray-200" />
                    No invoices found.
                  </td>
                </tr>
              ) : filtered.map(inv => {
                const match = EXTENDED_MATCHES.find(m => m.invoiceId === inv.id);
                const pmtStyle = PAYMENT_STATUS_STYLES[inv.paymentStatus] || PAYMENT_STATUS_STYLES.unpaid;
                return (
                  <tr
                    key={inv.id}
                    className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                    onClick={() => setSelected(inv)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-bold text-gray-900 text-xs">{inv.invoiceNumber}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{inv.invoiceDate}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold text-gray-800">{inv.supplier}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-semibold text-blue-600">{inv.poNumber}</div>
                      <div className="text-xs text-teal-600 mt-0.5">{inv.grnNumber}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <MatchResultIcon result={inv.matchStatus} />
                        <span className={`text-xs font-semibold ${
                          inv.matchStatus === 'matched' ? 'text-green-700' :
                          inv.matchStatus === 'minor_variance' ? 'text-amber-700' :
                          inv.matchStatus === 'mismatch'? 'text-red-700' : 'text-gray-500'
                        }`}>
                          {inv.matchStatus === 'matched' ? 'Matched' :
                           inv.matchStatus === 'minor_variance' ? 'Minor Var.' :
                           inv.matchStatus === 'mismatch' ? 'Mismatch' : 'Pending'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="text-sm font-bold text-gray-900">{formatUGX(inv.total)}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {match && match.totalVariance !== 0 ? (
                        <VarianceCell value={match.totalVariance} pct={match.variancePct} />
                      ) : (
                        <span className="text-xs text-green-600 font-semibold">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <InvoiceStatusBadge status={inv.status} />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${pmtStyle.bg} ${pmtStyle.text}`}>
                        {pmtStyle.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-600">{inv.dueDate}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => setSelected(inv)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-500"
                          title="View details"
                        >
                          <Eye size={14} />
                        </button>
                        {canApprove && (inv.status === 'pending' || inv.status === 'matched' || inv.status === 'minor_variance') && (
                          <button
                            onClick={() => setApprovalTarget(inv)}
                            className="p-1.5 rounded-lg hover:bg-orange-50 transition-colors text-orange-500"
                            title="Process approval"
                          >
                            <CheckCircle2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer summary */}
        {filtered.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50 flex flex-wrap gap-4 text-xs text-gray-500">
            <span>{filtered.length} invoice{filtered.length !== 1 ? 's' : ''}</span>
            <span>Total: <strong className="text-gray-800">{formatUGX(filtered.reduce((s, i) => s + i.total, 0))}</strong></span>
            <span>Unpaid: <strong className="text-orange-700">{formatUGX(filtered.filter(i => i.paymentStatus === 'unpaid').reduce((s, i) => s + i.total, 0))}</strong></span>
          </div>
        )}
      </div>

      {/* Drawers & Modals */}
      {selected && selectedMatch && (
        <MatchDrawer
          invoice={selected}
          match={selectedMatch}
          onClose={() => setSelected(null)}
          onApprove={() => { setApprovalTarget(selected); setSelected(null); }}
        />
      )}

      {approvalTarget && approvalMatch && (
        <ApprovalModal
          invoice={approvalTarget}
          match={approvalMatch}
          onClose={() => setApprovalTarget(null)}
          onAction={handleApprovalAction}
        />
      )}
    </div>
  );
}
